/**
 * Diccionario de colores → hex para pintar swatches en la UI.
 * Cubre los colores más comunes que usa el catálogo Beem (papel coreano, cintas,
 * botones, hilos, etc.). Para colores desconocidos se calcula un hex determinístico
 * a partir del hash del nombre — así siempre se muestra algo pero consistente.
 *
 * Normalización: uppercase, sin acentos, sin espacios.
 */
const CATALOGO_HEX = {
  BLANCO: "#F5F5F5",
  NEGRO: "#111111",
  GRIS: "#8A8A8A",
  GRISCLARO: "#C7C7C7",
  GRISOSCURO: "#4A4A4A",
  PLATA: "#C0C0C0",
  PLATEADO: "#C0C0C0",
  DORADO: "#D4AF37",
  ORO: "#D4AF37",
  BEIGE: "#E8DCC4",
  CREMA: "#EFE4B0",
  MARFIL: "#F1EAD8",
  MOSTAZA: "#D6A400",
  AMARILLO: "#F5D019",
  AMARILLOSUAVE: "#FFF3B0",
  NARANJA: "#F26522",
  ROJO: "#D62828",
  ROJOOSCURO: "#8B0000",
  VINO: "#7B1E3A",
  BORGONA: "#7B1E3A",
  BURDEOS: "#7B1E3A",
  ROSA: "#F498B6",
  ROSAFUERTE: "#EC4899",
  ROSAFUERT: "#EC4899",
  ROSABEBE: "#FBDDE3",
  ROSAPASTEL: "#FBDDE3",
  ROSAPALIDO: "#FBDDE3",
  ROSABABY: "#FBDDE3",
  ROSADO: "#F498B6",
  FUCSIA: "#E30B5D",
  LILA: "#B18CE6",
  MORADO: "#7A3EA0",
  VIOLETA: "#7A3EA0",
  PURPURA: "#7A3EA0",
  AZUL: "#1E5FCC",
  AZULCLARO: "#77B5FE",
  AZULCIELO: "#77B5FE",
  AZULOSCURO: "#0B2452",
  AZULMARINO: "#0B2452",
  AZULREY: "#134BD1",
  AZULBEBE: "#B8D8F8",
  AZULPASTEL: "#B8D8F8",
  CELESTE: "#77B5FE",
  TURQUESA: "#25B6B8",
  AGUAMARINA: "#7DE0D3",
  MENTA: "#9DE3B9",
  VERDE: "#2FA84F",
  VERDECLARO: "#8CD09B",
  VERDEOSCURO: "#0F5F2B",
  VERDELIMON: "#D0E82C",
  VERDEMILITAR: "#4B5320",
  VERDEBOTELLA: "#0B4D3B",
  OLIVA: "#7E7B37",
  CAFE: "#6B3E14",
  CAFECLARO: "#B67D4C",
  MARRON: "#6B3E14",
  CHOCOLATE: "#3E2C1E",
  TERRACOTA: "#C46A48",
  MULTICOLOR: "#TRANSPARENT",
  TRANSPARENTE: "#TRANSPARENT",
  NATURAL: "#E8DCC4",
  CRUDO: "#EAD9B2",
  CORAL: "#F98676",
  SALMON: "#FA8072",
  DURAZNO: "#F9C0A0",
};

function _norm(name) {
  if (!name) return "";
  return String(name)
    .toUpperCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^A-Z0-9]/g, "")
    .trim();
}

function _hashHex(name) {
  // Hash determinístico simple → hex pastel medio
  let h = 0;
  for (let i = 0; i < name.length; i += 1) {
    h = (h * 31 + name.charCodeAt(i)) & 0xffffff;
  }
  // Ajustar a un tono medio (no demasiado claro ni oscuro)
  const r = ((h >> 16) & 0x7f) + 0x40;
  const g = ((h >> 8) & 0x7f) + 0x40;
  const b = (h & 0x7f) + 0x40;
  return `#${((r << 16) | (g << 8) | b).toString(16).padStart(6, "0").toUpperCase()}`;
}

/**
 * Retorna el hex para pintar un swatch dado un nombre de color.
 * Si es "multicolor" o "transparente", retorna null (se usa un patrón).
 */
export function colorToHex(name) {
  const n = _norm(name);
  if (!n) return null;
  if (n === "MULTICOLOR" || n === "TRANSPARENTE") return null;
  // Buscar exact match, luego prefix (ej "AZULMARINO" → AZULMARINO; "AZULOCEANO" → cae al hash)
  if (CATALOGO_HEX[n]) return CATALOGO_HEX[n];
  // Buscar por prefijos comunes (ROSAFUERT vs ROSAFUERTE)
  for (const key of Object.keys(CATALOGO_HEX)) {
    if (n.startsWith(key) || key.startsWith(n)) return CATALOGO_HEX[key];
  }
  return _hashHex(n);
}

/**
 * Componente swatch reutilizable. Renderiza un círculo con el color detectado
 * y muestra un patrón multicolor para colores especiales.
 */
export function ColorSwatch({ nombre, size = 12, title = null, className = "" }) {
  const hex = colorToHex(nombre);
  const n = _norm(nombre);
  const isMulti = n === "MULTICOLOR";
  const isTransp = n === "TRANSPARENTE";
  const isWhite = hex && ["#F5F5F5", "#EFE4B0", "#F1EAD8"].includes(hex);
  const style = isMulti
    ? { background: "conic-gradient(from 0deg, #ec4899, #f5d019, #2fa84f, #1e5fcc, #ec4899)" }
    : isTransp
      ? { background: "repeating-conic-gradient(#e5e7eb 0% 25%, #f9fafb 0% 50%) 50% / 8px 8px" }
      : { background: hex || "#c4c4c4" };
  return (
    <span
      title={title || nombre}
      className={`inline-block rounded-full flex-shrink-0 ${isWhite ? "border border-border" : "border border-black/10"} ${className}`}
      style={{ width: size, height: size, ...style }}
      data-testid={`swatch-${(nombre || "").substring(0, 12)}`}
    />
  );
}
